import { isAccommodation } from "@@/shared/acquire/is-accommodation";
import { isBookable } from "@@/shared/acquire/is-bookable";
import type { ResourceReservation } from "@@/shared/acquire/resources/resource-reservation";
import { externalResourceReservationSourceMeta } from "@@/shared/acquire/resources/resource-reservation";
import {
    changeCaseForTranslatable,
    joinTranslatables,
} from "@@/shared/translations/text-utils";
import type { Translatable } from "@@/shared/translations/translation";
import { translation } from "@@/shared/translations/translation";
import type { Order } from "../order";
import { isOrderItemAccommodation, isOrderItemBookable } from "./is-order-item";
import { parseLatestResourceReservationComment } from "./parse-latest-resource-reservation-comment";

const parseMainOrderItemTitleForResourceReservation = (
    order: Order | undefined,
    reservation: ResourceReservation,
): Translatable => {
    if (isBookable(order)) {
        const bookable = order?.orderItems.find(isOrderItemBookable);
        const title = bookable?.meta.title;

        if (!title) return "";
        if (typeof title === "string") return title;

        if (reservation.resourceType === "RESOURCE_TABLE")
            return translation({
                sv: `${reservation.quantity} st ${title.sv}`,
                en: title.en,
            });
        return title;
    }

    if (isAccommodation(order)) {
        const accommodation = order?.orderItems.find(isOrderItemAccommodation);
        const title = accommodation?.meta.title;
        if (!title) return "";
        if (typeof title === "string") return title;
        return title;
    }

    switch (reservation.type) {
        case "DROP_IN": {
            const comment = parseLatestResourceReservationComment(reservation);
            if (comment) {
                return "DROP IN | " + comment;
            }

            return "DROP IN";
        }
        case "BLOCKED":
            switch (reservation.subType) {
                case "EXTERNAL_THIRD_PARTY_ORDER": {
                    if (reservation.source === "generic") {
                        const id = reservation.externalOrderId;
                        if (id) {
                            return changeCaseForTranslatable(
                                joinTranslatables([
                                    id,
                                    translation({
                                        sv: " (EXTERN)",
                                        en: " (EXTERNAL)",
                                    }),
                                ]),
                                "upper",
                            );
                        }
                        return translation({
                            sv: "EXTERN",
                            en: "EXTERNAL",
                        });
                    }

                    return changeCaseForTranslatable(
                        joinTranslatables([
                            externalResourceReservationSourceMeta[
                                reservation.source
                            ].title,
                            translation({
                                sv: ` (EXTERN)`,
                                en: ` (EXTERNAL)`,
                            }),
                        ]),
                        "upper",
                    );
                }
                case "EXTERNAL_ICAL": {
                    const comment =
                        parseLatestResourceReservationComment(reservation);
                    if (comment) {
                        return comment;
                    }

                    return changeCaseForTranslatable(
                        joinTranslatables([
                            externalResourceReservationSourceMeta[
                                reservation.source
                            ].title,
                            translation({
                                sv: ` (ICAL)`,
                                en: ` (ICAL)`,
                            }),
                        ]),
                        "upper",
                    );
                }

                default: {
                    const comment =
                        parseLatestResourceReservationComment(reservation);
                    if (comment) {
                        return comment;
                    }

                    return translation({
                        sv: "BLOCKERAD",
                        en: "BLOCKED",
                    });
                }
            }
        default:
            return "";
    }
};

export { parseMainOrderItemTitleForResourceReservation };
